import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetslipConfig, EpcotConfig, LineSwitcherConfig, PriceBoostConfig } from '@frontend/sports/common/client-config-data-access';
import { Store } from '@ngrx/store';
import { UiManagerActions } from 'packages/sports/web/app/src/ui-manager/ui-manager.actions';
import { IUiRootState } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { take } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../../core/picks/betslip-v2-option-market-pick';
import { LineSwitcherActions } from '../../../line-switcher/actions';
import { QuickBetService } from '../../../quick-bet/services/quick-bet.service';
import { betslipTypeStateSelector } from '../../../types/selectors';

@Component({
    selector: 'bs-line-switcher-button',
    templateUrl: './line-switcher-button.component.html',
    styleUrls: ['./line-switcher-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LineSwitcherButtonComponent {
    @Input() pick: BetslipPick;
    inEditBet = false;
    iconSize: number;

    constructor(
        private betslipConfig: BetslipConfig,
        private lineSwitcherConfig: LineSwitcherConfig,
        private store: Store<IBetslipRootState & IUiRootState>,
        private priceBoostConfig: PriceBoostConfig,
        epcotConfig: EpcotConfig,
        private quickbetService: QuickBetService,
    ) {
        this.store
            .select(betslipTypeStateSelector)
            .pipe(take(1), takeUntilDestroyed())
            .subscribe((state) => {
                this.inEditBet = state.base.currentSelectedType === BetslipType.EditBet;
                this.iconSize = epcotConfig.isEnabled && !this.inEditBet ? 8 : 12;
            });
    }

    get shouldShowLineSwitcher(): boolean {
        const hasLineSwitcher =
            this.pick &&
            this.betslipConfig.isLineSwitcherEnabled &&
            !this.lineSwitcherConfig.disabledSports.includes(this.pick.sportId) &&
            this.checkEnabledLeagues(this.pick) &&
            !this.quickbetService.isQuickBetVisible;

        return this.priceBoostConfig.isEnabled ? hasLineSwitcher && !this.pick?.boostedPriceDetails?.isPriceBoosted : hasLineSwitcher;
    }

    get isDisabled(): boolean {
        return !this.pick.isAvailable || !this.pick.market.isVisible;
    }

    onClick(event: MouseEvent): void {
        this.store.dispatch(UiManagerActions.removeState({ state: UiStates.MarketSubTypeTooltipShown }));

        event.stopPropagation();
        if (!this.inEditBet || !this.isDisabled) {
            this.store.dispatch(LineSwitcherActions.initLineSwitcher({ pick: this.pick }));
        }
    }

    private checkEnabledLeagues(pick: BetslipPick): boolean {
        const leagueId = BetslipV1Pick.isPick(pick) ? pick.league.id : BetslipV2OptionMarketPick.isPick(pick) ? pick.fixture.league?.id : undefined;
        if (!leagueId) {
            return false;
        }

        return !this.lineSwitcherConfig.disabledLeagues.includes(leagueId);
    }
}
